import { useCallback, useEffect, useRef } from 'react';
import type { GroupWithTicketTypeIds, PracticeWithTicketTypeIds } from '../../common/types';
import { useActionsProvider } from '../../services/store';
import useTracking from '../tracking';

type Relationships = {
	groups: GroupWithTicketTypeIds[];
	practices: PracticeWithTicketTypeIds[];
};

const useInit = () => {
	const { getGroups, getTicketTypes, setRelationships, cleanup } = useActionsProvider();
	const isGroupsCompleteRef = useRef<boolean>(false);
	const relationshipsRef = useRef<Relationships>();
	const { fireError, fireOperational } = useTracking();

	const finalizeInit = useCallback(() => {
		const { current: isGroupsComplete } = isGroupsCompleteRef;
		const { current: relationships } = relationshipsRef;

		if (isGroupsComplete && relationships) {
			setRelationships(relationships.groups, relationships.practices);
		}
	}, [setRelationships]);

	useEffect(() => {
		getGroups((_, error) => {
			if (error) {
				fireError('getGroups', error);
			} else {
				isGroupsCompleteRef.current = true;
				finalizeInit();
				fireOperational('getPortalGroups fetchSuccess');
			}
		});

		getTicketTypes((relationships, error) => {
			if (error) {
				fireError('getTicketTypes', error);
			} else if (relationships) {
				relationshipsRef.current = relationships;
				finalizeInit();
				fireOperational('getPortalTicketTypes fetchSuccess');
			}
		});

		return () => {
			cleanup();
		};
	}, [cleanup, finalizeInit, fireError, fireOperational, getGroups, getTicketTypes]);
};

export default useInit;
